<template>
  <div class="event-filter">
    <!-- Filter Bar -->
    <div class="custom-filter-bar">
      <div class="filter-item" :class="{ active: isProductActive }" @click="showProductPopup = true">
        <span class="filter-label txt-ellipsis">{{ displayLabel }}</span>
        <van-icon name="play" class="filter-arrow" />
      </div>
      <div v-if="typeOptions.length > 0" class="filter-item" :class="{ active: isTypeActive }" @click="showTypePopup = true">
        <span class="filter-label txt-ellipsis">{{ displayTypeLabel }}</span>
        <van-icon name="play" class="filter-arrow" />
      </div>
      <div class="filter-item" :class="{ active: isTimeActive }" @click="showTimePopup = true">
        <span class="filter-label txt-ellipsis">{{ displayTimeLabel }}</span>
        <van-icon name="play" class="filter-arrow" />
      </div>
      <div v-if="isScopeUser" class="filter-item" :class="{ active: isShowValActive }" @click="showScopePopup = true">
        <span class="filter-label txt-ellipsis">{{ showVal || $t('shared.filter.all') }}</span>
        <van-icon name="play" class="filter-arrow" />
      </div>
    </div>

    <!-- Type Filter Popup -->
    <van-popup v-model="showTypePopup" position="bottom" round safe-area-inset-bottom>
      <div class="filter-popup-content">
        <div class="filter-title">{{ $t('shared.filter.typeFilter') }}</div>
        <div class="filter-options">
          <div
            v-for="option in typeOptions"
            :key="option.value"
            class="filter-tag"
            :class="{ active: tempFilterType === option.value }"
            @click="toggleTypeSelection(option.value)"
          >
            {{ option.label }}
          </div>
        </div>
        <div class="filter-actions">
          <van-button block class="reset-btn" @click="resetTypeFilter">{{ $t('button.reset') }}</van-button>
          <van-button block type="info" class="confirm-btn" @click="confirmTypeFilter">{{ $t('button.confirm') }}</van-button>
        </div>
      </div>
    </van-popup>

    <!-- Product Filter Popup -->
    <van-popup v-model="showProductPopup" position="bottom" round safe-area-inset-bottom>
      <div class="filter-popup-content">
        <div class="filter-title">{{ $t('shared.filter.filter') }}</div>
        <div class="filter-options">
          <div
            v-for="option in productOptions"
            :key="option[valueKey]"
            class="filter-tag"
            :class="{ active: isSelected(option[valueKey]) }"
            @click="toggleSelection(option[valueKey])"
          >
            {{ option[labelKey] }}
          </div>
        </div>
        <div class="filter-actions">
          <van-button block class="reset-btn" @click="resetProductFilter">{{ $t('button.reset') }}</van-button>
          <van-button block type="info" class="confirm-btn" @click="confirmProductFilter">{{ $t('button.confirm') }}</van-button>
        </div>
      </div>
    </van-popup>

    <!-- Time Filter Popup -->
    <van-popup v-model="showTimePopup" position="bottom" round safe-area-inset-bottom>
      <div class="filter-popup-content">
        <div class="filter-title">{{ $t('shared.filter.timeFilter') }}</div>
        <div class="time-selector">
          <div class="time-input" @click="showStartDatePicker = true">
            <span v-if="tempStartDate">{{ tempStartDate }}</span>
            <span v-else class="placeholder">{{ $t('shared.filter.startDate') }}</span>
          </div>
          <span class="separator">~</span>
          <div class="time-input" @click="showEndDatePicker = true">
            <span v-if="tempEndDate">{{ tempEndDate }}</span>
            <span v-else class="placeholder">{{ $t('shared.filter.endDate') }}</span>
          </div>
        </div>
        <div class="filter-actions">
          <van-button block class="reset-btn" @click="resetTimeFilter">{{ $t('button.reset') }}</van-button>
          <van-button block type="info" class="confirm-btn" @click="confirmTimeFilter">{{ $t('button.confirm') }}</van-button>
        </div>
      </div>
    </van-popup>

    <!-- Date Pickers (Popups) -->
    <van-popup v-model="showStartDatePicker" position="bottom">
      <van-datetime-picker
        v-model="currentStartDate"
        type="date"
        :title="$t('shared.filter.selectStartDate')"
        @confirm="onConfirmStartDate"
        @cancel="showStartDatePicker = false"
      />
    </van-popup>
    <van-popup v-model="showEndDatePicker" position="bottom">
      <van-datetime-picker
        v-model="currentEndDate"
        type="date"
        :title="$t('shared.filter.selectEndDate')"
        @confirm="onConfirmEndDate"
        @cancel="showEndDatePicker = false"
      />
    </van-popup>
    <van-popup v-model="showScopePopup" position="bottom" :round="true" :style="{ 'max-height': '566px', 'min-height': '266px' }" get-container="#app">
      <div class="other-content">
        <div class="filter-popup-title">{{ $t('shared.filter.filter') }}</div>
        <div class="filter-scope-content" style="max-height: 436px; min-height: 266px">
          <div v-for="(otherItem, otherIndex) in dataOptions" :class="{'active-status': selectVal.value.includes(otherItem[fieldNames.value]) }" @click="changeVal(otherItem)" :key="otherIndex" class="item-content">
            {{ otherItem[fieldNames.label] }}
          </div>
        </div>
        <div class="operate-btn">
          <van-button type="primary" @click="filterOtherReset">{{ $t('button.reset') }}</van-button>
          <van-button class="confirm-btn" type="primary" @click="confirmFilter">{{ $t('button.confirm') }}</van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'EventFilter',
  props: {
    productOptions: {
      type: Array,
      default: () => []
    },
    // 'single' or 'multiple'
    selectionMode: {
      type: String,
      default: 'single'
    },
    // Custom field names mapping
    objKey: {
      type: Object,
      default: () => ({ label: 'label', value: 'value' })
    },
    pageType: {
      type: String,
      default: ''
    },
    isScopeUser: {
      type: Boolean,
      default: false
    },
    typeOptions: {
      type: Array,
      default: () => []
    },
    dataOptions: {
      type: Array,
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: false
    },
    fieldNames: {
      type: Object,
      default: () => {
        return {
          label: 'label',
          value: 'value'
        }
      }
    }
  },
  data () {
    return {
      // Popup visibility
      showProductPopup: false,
      showTimePopup: false,
      showStartDatePicker: false,
      showEndDatePicker: false,
      showScopePopup: false,
      showTypePopup: false,
      selectVal: {
        label: [],
        value: []
      },
      showVal: '',

      // Product Filter State
      // For single: string; For multiple: array
      tempFilterProduct: this.selectionMode === 'multiple' ? [] : '',
      confirmedFilterProduct: this.selectionMode === 'multiple' ? [] : '',

      // Type Filter State
      tempFilterType: '',
      confirmedFilterType: '',

      // Time Filter State
      tempStartDate: '',
      tempEndDate: '',
      confirmedStartDate: '',
      confirmedEndDate: '',
      currentStartDate: new Date(),
      currentEndDate: new Date(),
      showLabel: 'Product'
    }
  },
  computed: {
    labelKey () {
      // Compatible with user's request: objKey={key:'name', value:'id'}
      // where 'key' maps to display label
      return this.objKey.label || this.objKey.key || 'label'
    },
    valueKey () {
      return this.objKey.value || 'value'
    },
    displayLabel () {
      const showLabel = []
      if (this.confirmedFilterProduct || this.confirmedFilterProduct === 0) {
        this.productOptions.forEach(subItem => {
          if (this.selectionMode === 'multiple') {
            if (this.confirmedFilterProduct.includes(subItem[this.valueKey])) {
              showLabel.push(subItem[this.labelKey])
            }
          } else {
            if (this.confirmedFilterProduct === subItem[this.valueKey]) {
              showLabel.push(subItem[this.labelKey])
            }
          }
        })
      }
      if (showLabel.length > 0) {
        return showLabel.join(',')
      }
      return this.pageType === 'incentive' ? this.$t('shared.filter.all') : this.pageType === 'mapping' ? this.$t('shared.filter.allType') : this.$t('shared.filter.product')
    },
    displayTypeLabel () {
      if (this.confirmedFilterType || this.confirmedFilterType === 0) {
        const found = this.typeOptions.find(subItem => subItem[this.valueKey] === this.confirmedFilterType)
        if (found) {
          return found[this.labelKey]
        }
      }
      return this.$t('shared.filter.type')
    },
    displayTimeLabel () {
      if (this.confirmedStartDate && this.confirmedEndDate) {
        return `${this.confirmedStartDate}~${this.confirmedEndDate}`
      } else if (this.confirmedStartDate) {
        return `${this.confirmedStartDate}~`
      } else if (this.confirmedEndDate) {
        return `~${this.confirmedEndDate}`
      }
      return this.$t('shared.filter.time')
    },
    isProductActive () {
      if (this.selectionMode === 'multiple') {
        return this.confirmedFilterProduct && this.confirmedFilterProduct.length > 0
      }
      // 兼容 0 的情况
      return this.confirmedFilterProduct || this.confirmedFilterProduct === 0
    },
    isTimeActive () {
      return !!this.confirmedStartDate || !!this.confirmedEndDate
    },
    isShowValActive () {
      return this.showVal
    },
    isTypeActive () {
      return this.confirmedFilterType || this.confirmedFilterType === 0
    }
  },
  methods: {
    // --- Type Selection Logic ---
    toggleTypeSelection (value) {
      this.tempFilterType = this.tempFilterType === value ? '' : value
    },
    resetTypeFilter () {
      this.tempFilterType = ''
      this.confirmTypeFilter()
    },
    confirmTypeFilter () {
      this.confirmedFilterType = this.tempFilterType
      this.showTypePopup = false
      this.$emit('filter-change', {
        product: this.confirmedFilterProduct,
        startDate: this.confirmedStartDate,
        endDate: this.confirmedEndDate,
        userIdList: this.selectVal.value,
        type: this.confirmedFilterType
      })
    },

    // --- Product Selection Logic ---
    isSelected (value) {
      if (this.selectionMode === 'multiple') {
        return this.tempFilterProduct.includes(value)
      }
      return this.tempFilterProduct === value
    },
    toggleSelection (value) {
      if (this.selectionMode === 'multiple') {
        const index = this.tempFilterProduct.indexOf(value)
        if (index > -1) {
          this.tempFilterProduct.splice(index, 1)
        } else {
          this.tempFilterProduct.push(value)
        }
      } else {
        // Toggle off if clicking the same item, otherwise select new
        this.tempFilterProduct = this.tempFilterProduct === value ? '' : value
      }
    },
    resetProductFilter () {
      this.tempFilterProduct = this.selectionMode === 'multiple' ? [] : ''
      this.confirmProductFilter()
    },
    confirmProductFilter () {
      // Deep copy if array to avoid reference issues
      if (this.selectionMode === 'multiple') {
        this.confirmedFilterProduct = [...this.tempFilterProduct]
      } else {
        this.confirmedFilterProduct = this.tempFilterProduct
      }
      this.showProductPopup = false
      this.$emit('filter-change', {
        product: this.confirmedFilterProduct,
        startDate: this.confirmedStartDate,
        endDate: this.confirmedEndDate,
        userIdList: this.selectVal.value,
        type: this.confirmedFilterType
      })
    },

    // --- Time Selection Logic ---
    resetTimeFilter () {
      this.tempStartDate = ''
      this.tempEndDate = ''
    },
    confirmTimeFilter () {
      this.confirmedStartDate = this.tempStartDate
      this.confirmedEndDate = this.tempEndDate
      this.showTimePopup = false
      this.$emit('filter-change', {
        product: this.confirmedFilterProduct,
        startDate: this.confirmedStartDate,
        endDate: this.confirmedEndDate,
        userIdList: this.selectVal.value,
        type: this.confirmedFilterType
      })
    },
    onConfirmStartDate (val) {
      this.tempStartDate = moment(val).format('YYYY-MM-DD')
      this.showStartDatePicker = false
    },
    onConfirmEndDate (val) {
      this.tempEndDate = moment(val).format('YYYY-MM-DD')
      this.showEndDatePicker = false
    },
    changeVal (params) {
      if (this.multiple) {
        // 如果已经选中，再次点击即删除
        if (this.selectVal.value.includes(params[this.fieldNames.value])) {
          this.selectVal.value = this.selectVal.value.filter((subItem) => {
            return subItem !== params[this.fieldNames.value]
          })
          this.selectVal.label = this.selectVal.label.filter((subItem) => {
            return subItem !== params[this.fieldNames.label]
          })
        } else {
          this.selectVal.label.push(params[this.fieldNames.label])
          this.selectVal.value.push(params[this.fieldNames.value])
        }
      } else {
        this.selectVal.label = [params[this.fieldNames.label]]
        this.selectVal.value = [params[this.fieldNames.value]]
      }
    },
    filterOtherReset () {
      this.selectVal = {
        label: [],
        value: []
      }
      this.showVal = ''
      this.confirmFilter()
      this.showScopePopup = false
    },
    confirmFilter () {
      this.showVal = this.selectVal.label.join(',')
      this.$emit('filter-change', {
        product: this.confirmedFilterProduct,
        startDate: this.confirmedStartDate,
        endDate: this.confirmedEndDate,
        userIdList: this.selectVal.value,
        type: this.confirmedFilterType
      })
      this.showScopePopup = false
    }
  },
  watch: {
    // Sync internal state if needed when popup opens
    showProductPopup (val) {
      if (val) {
        // Reset temp to confirmed state when reopening
        if (this.selectionMode === 'multiple') {
          this.tempFilterProduct = [...this.confirmedFilterProduct]
        } else {
          this.tempFilterProduct = this.confirmedFilterProduct
        }
      }
    },
    showTimePopup (val) {
      if (val) {
        this.tempStartDate = this.confirmedStartDate
        this.tempEndDate = this.confirmedEndDate
      }
    },
    showTypePopup (val) {
      if (val) {
        this.tempFilterType = this.confirmedFilterType
      }
    }
  }
}
</script>

<style lang="less" scoped>
.custom-filter-bar {
  display: flex;
  align-items: center;
  height: 36px;
  background: #fff;
  padding: 0 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);

  .filter-item {
    display: flex;
    align-items: center;
    margin-right: 16px;
    font-size: 13px;
    color: #333;
    cursor: pointer;
    &:last-child {
      margin-right: 0px;
    }

    .filter-label {
      max-width: 160px;
    }

    .filter-arrow {
      font-size: 10px;
      margin-left: 4px;
      transform: rotate(90deg);
      color: #333;
      transition: transform 0.2s;
    }

    &.active {
      color: #1989fa;
      .filter-arrow {
        color: #1989fa;
      }
    }
  }
}

.filter-popup-content {
  padding: 16px;
  background: #fff;
  min-height: 200px;
  display: flex;
  flex-direction: column;

  .filter-title {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 20px;
    text-align: center;
    color: #333;
  }

  .filter-options {
    display: flex;
    flex-wrap: wrap;
    max-height: 316px;
    min-height: 156px;
    gap: 10px;
    margin-bottom: 30px;
    flex: 1;
    overflow-y: auto;

    .filter-tag {
      padding: 0px 16px;
      background: #f5f6f8;
      border-radius: 4px;
      font-size: 12px;
      color: #333;
      cursor: pointer;
      min-width: 80px;
      height: 30px;
      line-height: 30px;
      text-align: center;

      &.active {
        background: #e6f1fc;
        color: #1989fa;
        font-weight: 500;
      }
    }
  }

  .time-selector {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 30px;
    flex: 1;

    .time-input {
      flex: 1;
      height: 40px;
      background: #f5f6f8;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      color: #333;

      .placeholder {
        color: #999;
      }
    }

    .separator {
      margin: 0 10px;
      color: #666;
    }
  }

  .filter-actions {
    display: flex;
    gap: 12px;
    margin-top: auto;

    .reset-btn {
      flex: 1;
      background: #ebedf0;
      border: none;
      color: #333;
      border-radius: 6px;
    }

    .confirm-btn {
      flex: 1;
      border-radius: 6px;
    }
  }
}

.other-content {
  padding: 10px 18px 20px 18px;
  .filter-popup-title {
    font-weight: bold;
    font-size: 16px;
    text-align: center;
    margin-bottom: 10px;
  }
  .filter-scope-content {
    display: flex;
    flex-wrap: wrap;
    margin-top: 10px;
    align-content: start;
    overflow: auto;
    .item-title {
      width: 100%;
      color: #000;
      font-weight: bold;
      font-size: 12px;
      padding: 7px 0;
    }
    .item-content {
      width: 48%;
      height: 27px;
      color: #000;
      text-align: center;
      line-height: 27px;
      font-size: 11px;
      background-color: #f3f6fc;
      border-radius: 6px;
      margin-top: 5px;
      margin-bottom: 5px;
      // padding: 0 5px;
      margin-right: 5px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .active-status {
      background-color: #1989fa;
      color: #fff;
    }
    .project-item {
      display: flex;
      align-items: center;
      padding: 10px;
      height: 47px;
      font-size: 12px;
      border-bottom: 1px solid #dce5f6;
    }
    .choice-active {
      color: #307bff;
    }
  }
  .operate-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 20px;
    justify-content: space-between;
    /deep/.van-button {
      width: 126px;
      height: 40px;
      background-color: #dce5f6;
      border: 1px solid #dce5f6;
      border-radius: 8px;
      color: #000;
    }
    .confirm-btn {
      width: 190px;
      color: #fff;
      background-color: #307bff;
      border: 1px solid #307bff;
    }
  }
}
</style>
